import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import toast from "react-hot-toast";
import saleService from "../../../Services/saleService";
import InvoiceModalbyMedicine from "../../../Components/IvoiceModalbyMedicine";

const Sales = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [patients, setPatients] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const getSales = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllMedicinesSales();
        const sales = response.sales || [];
        setPatients(sales);
        const grouped = sales.reduce((acc, item) => {
          if (!acc[item.invoice]) acc[item.invoice] = [];
          acc[item.invoice].push(item);
          return acc;
        }, {});
        setGroupedData(grouped);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching Sales");
      }
    };
    getSales();
  }, []);

  useEffect(() => {
    const filteredResult = Object.entries(groupedData)
      .filter(([invoice, items]) => {
        return items.some((item) => {
          const itemDate = new Date(item.sale_date).setHours(0, 0, 0, 0);
          const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
          const end = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;
          const isWithinDateRange =
            (!start || start <= itemDate) && (!end || itemDate <= end);
          return (
            item?.patient_name?.toLowerCase().includes(searchData.toLowerCase()) &&
            isWithinDateRange
          );
        });
      })
      .map(([invoice, items]) => ({ invoice, items }));
    setFilteredData(filteredResult);
    setCurrentPage(1);
  }, [searchData, startDate, endDate, groupedData]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  }, [currentPage, filteredData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const grandTotal = filteredData
    .reduce(
      (sum, { items }) =>
        sum + items.reduce((acc, item) => acc + item.quantity * item.unit_price, 0),
      0
    )
    .toFixed(2);

  return (
  <div className="p-3 sm:p-6">

    <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-4 sm:mb-5 shadow-sm">

  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

    <div>
      <h1 className="text-[15px] sm:text-[17px] font-[500] text-gray-800">
        Medicine Sales Record
      </h1>
      <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
        {filteredData.length || 0} records found
      </p>
    </div>

    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">

      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        className="w-full sm:w-auto text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
      />

      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        className="w-full sm:w-auto text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
      />

      {(startDate || endDate) && (
        <button
          onClick={() => {
            setStartDate("");
            setEndDate("");
          }}
          className="w-full sm:w-auto text-[13px] bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-[7px] rounded-lg"
        >
          Show All
        </button>
      )}

      <div className="relative w-full sm:w-[200px]">
        <SearchIcon
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
          style={{ fontSize: 16 }}
        />
        <input
          type="search"
          placeholder="Search by patient..."
          value={searchData}
          onChange={(e) => setSearchData(e.target.value)}
          className="pl-8 pr-3 py-[7px] w-full text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
        />
      </div>

    </div>
  </div>
</div>

    <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">

          <thead>
            <tr className="bg-[#2F6DB4] text-[11px] uppercase text-white">
              <th className="py-3 px-4 text-left">Invoice</th>
              <th className="py-3 px-4 text-left">Patient Name</th>
              <th className="py-3 px-4 text-left">Total Items</th>
              <th className="py-3 px-4 text-right">Total Amount</th>
              <th className="py-3 px-4 text-center">Sale Date</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-gray-400 text-[13px]">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-gray-400 text-[13px]">
                  No records found
                </td>
              </tr>
            ) : (
              paginatedData.map(({ invoice, items }, index) => (

                <tr
                  key={invoice}
                  className={`border-t text-[13px] hover:bg-gray-50 ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >

                  <td className="py-3 px-4 font-[500] text-gray-800">
                    {invoice}
                  </td>

                  <td className="py-3 px-4 text-gray-600">
                    {items[0]?.patient_name}
                  </td>

                  <td className="py-3 px-4 text-gray-600">
                    {items.length}
                  </td>

                  <td className="py-3 px-4 text-right font-semibold text-gray-800">
                    {items
                      .reduce(
                        (acc, item) =>
                          acc + item.quantity * item.unit_price,
                        0
                      )
                      .toFixed(2)}
                  </td>

                  <td className="py-3 px-4 text-center text-gray-600">
                    {formatDate(items[0]?.sale_date)}
                  </td>

                  <td className="py-3 px-4 text-center">
                    <button
                      className="bg-blue-50 text-blue-600 text-[12px] px-3 py-1 rounded-md hover:bg-blue-100"
                      onClick={() =>
                        setSelectedInvoice({
                          invoiceNumber: items[0]?.invoice,
                          patientName: items[0]?.patient_name,
                          doctorName: items[0]?.doctor_name,
                          patientAddress: items[0]?.patient_address,
                          sale_date: items[0]?.sale_date,
                          invoiceData: items,
                        })
                      }
                    >
                      View Invoice
                    </button>
                  </td>

                </tr>
              ))
            )}

          </tbody>
        </table>
      </div>
    </div>

    <div className="sm:hidden space-y-3">

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-400 text-[13px] shadow-sm">
          Loading...
        </div>
      ) : paginatedData.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-400 text-[13px] shadow-sm">
          No records found
        </div>
      ) : (
        paginatedData.map(({ invoice, items }) => {

          const totalAmount = items
            .reduce((acc, item) => acc + item.quantity * item.unit_price, 0)
            .toFixed(2);

          return (
            <div
              key={invoice}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
            >

              <div className="flex items-start justify-between mb-3">

                <div>
                  <span className="text-[10px] font-bold text-[#2F6DB4] uppercase">
                    Invoice #{invoice}
                  </span>

                  <p className="text-[14px] font-[600] text-gray-800">
                    {items[0]?.patient_name}
                  </p>
                </div>

                <button
                  className="bg-blue-50 text-blue-600 text-[11px] px-2.5 py-1 rounded-md hover:bg-blue-100"
                  onClick={() =>
                    setSelectedInvoice({
                      invoiceNumber: items[0]?.invoice,
                      patientName: items[0]?.patient_name,
                      doctorName: items[0]?.doctor_name,
                      patientAddress: items[0]?.patient_address,
                      sale_date: items[0]?.sale_date,
                      invoiceData: items,
                    })
                  }
                >
                  View
                </button>

              </div>

              <div className="grid grid-cols-3 gap-2">

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400">Items</p>
                  <p className="text-[13px] font-semibold">
                    {items.length}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400">Amount</p>
                  <p className="text-[13px] font-semibold">
                    {totalAmount}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-2 whitespace-nowrap ">
                  <p className="text-[10px] text-gray-400">Date</p>
                  <p className="text-[13px] font-semibold">
                    {formatDate(items[0]?.sale_date)}
                  </p>
                </div>

              </div>

            </div>
          );
        })
      )}

    </div>

    <PaginationComponent
      filteredData={filteredData}
      setPaginatedData={setPaginatedData}
      itemsPerPage={itemsPerPage}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
    />

    {selectedInvoice && (
      <InvoiceModalbyMedicine
        invoiceNumber={selectedInvoice.invoiceNumber}
        patientName={selectedInvoice.patientName}
        doctorName={selectedInvoice.doctorName}
        patientAddress={selectedInvoice.patientAddress}
        sale_date={selectedInvoice.sale_date}
        invoiceData={selectedInvoice.invoiceData}
        onClose={() => setSelectedInvoice(null)}
      />
    )}

  </div>
);
};

export default Sales;